import type {
  FreelancerBidParams,
  PublicFreelancerBidParams,
  ResumeAttachment,
} from "@js/types/jobs";
import { deepClone } from "@js/utils/data";
import { typeGuard } from "@js/utils/typescript";

export type FreelancerBidFormValues = Omit<FreelancerBidParams, "resume"> & {
  resume?: ResumeAttachment | number | null;
};

export type PublicFreelancerBidFormValues = FreelancerBidFormValues &
  Pick<
    PublicFreelancerBidParams,
    "first_name" | "email" | "last_name" | "linkedin_url" | "website_url"
  >;

export const parseCreateBidFormValues = <T extends FreelancerBidFormValues>(
  values: T,
) => {
  const parsedValues = deepClone(values);
  if (parsedValues["freelancer_availability"] !== "specific_date") {
    delete parsedValues["freelancer_availability_specific_date"];
  }

  const resumeValue =
    typeof parsedValues.resume === "number"
      ? parsedValues.resume
      : parsedValues.resume?.id;

  const bidParams = {
    ...parsedValues,
    resume: resumeValue,
  };

  return bidParams;
};

export const parseEditBidFormValues = <T extends FreelancerBidFormValues>(
  values: T,
) => {
  const parsedValues = deepClone(values);

  if (parsedValues["payment_amount"] === "") {
    parsedValues["payment_amount"] = undefined;
  }

  const resumeValue =
    typeof parsedValues.resume === "number"
      ? parsedValues.resume
      : parsedValues.resume?.id;

  const bidParams = {
    ...parsedValues,
    resume: resumeValue,
  };

  return bidParams;
};

type AnswerError = { answer?: string };

export const parseBidFormError = (error: unknown) => {
  if (!error || typeof error !== "object") {
    return;
  }

  if (!typeGuard<unknown, { data: Record<string, unknown> }>(error, "data")) {
    return;
  }

  const parsedError: Record<string, string | AnswerError[]> = {};
  Object.entries(error.data).forEach(([key, value]) => {
    if (typeof value === "string") {
      parsedError[key] = value;
      return;
    }

    if (!Array.isArray(value) || !value.length) {
      return;
    }

    if (key === "new_application_answers") {
      parsedError[key] = value.map((item: unknown) => {
        if (!item || typeof item !== "object") {
          return {};
        }

        if (typeGuard<object, { _error: string }>(item, "_error")) {
          return {
            answer: item._error,
          };
        }

        return item;
      });

      return;
    }

    parsedError[key] = value[0];
  });

  return parsedError;
};
